import React, { useState, useEffect } from "react";
import { GridColDef, GridCellParams } from "@mui/x-data-grid";
import { Box } from "@mui/material";
import { Formik, Form } from "formik";
import Grid from '@mui/material/Grid2';
import * as Yup from "yup";
import SearchIcon from "@mui/icons-material/Search";
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Dropdown from '../../components/Dropdown'
import TextField from '../../components/TextField';
import GridTable from "../../components/GridTable";
import ActionMenu from "../../components/ActionMenu";
import Dialog from "../../components/Dialog";
import Loader from "../../components/Loader";
import Button from "../../components/Button";
import { successMessage, errorMessage } from "../../components/Toaster";
import { getAllRoles, createRole, updateRole, deleteRole, getRoleNames } from "./api";
import { ApiResponse, RoleData } from './interface';
import { getErrorMessage } from "../../utils/error";

const Role: React.FC = () => {
    const [roles, setRoles] = useState<RoleData[]>([]);
    const [roleNames, setRoleNames] = useState<string[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [editRole, setEditRole] = useState<RoleData | null>(null);
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<GridCellParams | null>(null);
    const [loading, setLoading] = useState({
        fetch: false,
        create: false,
        update: false,
        delete: false,
    });

    const validationSchema = Yup.object().shape({
        roleName: Yup.string().required('Role name is required'),
        roleDescription: Yup.string().required('Role description is required'),
        displayName: Yup.string().required('Display name is required'),
    });

    const columns: GridColDef[] = [
        { field: 'role_name', headerName: 'Role Name', flex: 1 },
        { field: 'display_name', headerName: 'Display Name', flex: 1 },
        {
            field: "actions",
            headerName: "Actions",
            renderCell: (params: GridCellParams) => (
                <ActionMenu
                    params={params}
                    actions={[
                        {
                            icon: <EditIcon color="primary" />,
                            tooltip: "Edit",
                            onClick: () => handleEdit(params),
                        },
                        {
                            icon: <DeleteIcon color="error" />,
                            tooltip: "Delete",
                            onClick: () => handleDelete(params),
                        },
                    ]}
                />
            ),
            sortable: false,
            filterable: false,
            flex: 1,
        },
    ];

    useEffect(() => {
        const fetchRoles = async () => {
            setLoading((prev) => ({ ...prev, fetch: true }));
            try {
                const rolesData: ApiResponse<RoleData[]> = await getAllRoles();
                if (rolesData.status && rolesData.data) {
                    setRoles(rolesData.data);
                } else {
                    errorMessage(rolesData.message || "Failed to fetch roles.");
                }
            } catch (error) {
                console.error("Error fetching roles:", error);
                const message = getErrorMessage(error);
                errorMessage(message || "Error fetching roles");
            } finally {
                setLoading((prev) => ({ ...prev, fetch: false }));
            }
        };

        fetchRoles();
    }, []);

    const openDialog = async () => {
        setDialogOpen(true);
        setLoading((prev) => ({ ...prev, fetch: true }));
        try {
            const roleNamesResponse: ApiResponse<string[]> = await getRoleNames();
            if (roleNamesResponse.status && roleNamesResponse?.data) {
                setRoleNames(roleNamesResponse?.data);
            } else {
                errorMessage(roleNamesResponse.message || "Failed to fetch role names.");
            }
        } catch (error) {
            console.error("Error fetching role names:", error);
            const message = getErrorMessage(error);
            errorMessage(message || "Error fetching role names");
        } finally {
            setLoading((prev) => ({ ...prev, fetch: false }));
        }
    };

    const closeDialog = (resetForm: () => void) => {
        resetForm();
        setEditRole(null);
        setDialogOpen(false);
    };

    const handleEdit = (params: GridCellParams) => {
        setEditRole(params.row as RoleData);
        openDialog();
    };

    const handleDelete = (params: GridCellParams) => {
        setItemToDelete(params);
        setDeleteOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteOpen(false);
        setItemToDelete(null);
    };

    const confirmDialog = async (values: any, { resetForm }: { resetForm: () => void }) => {
        if (editRole) {
            await handleUpdate(values);
        } else {
            await handleCreate(values);
        }
        closeDialog(resetForm);
    };

    const handleCreate = async (values: { roleName: string; roleDescription: string; displayName: string }) => {
        setLoading((prev) => ({ ...prev, create: true }));
        try {
            const response = await createRole({
                role_name: values.roleName,
                role_description: values.roleDescription,
                display_name: values.displayName,
            });

            if (response.status) {
                const newRole = {
                    role_name: values.roleName,
                    role_description: values.roleDescription,
                    display_name: values.displayName,
                };
                setRoles((prevRoles) => [...prevRoles, newRole]);
                successMessage(response.message);
            } else {
                errorMessage(response.message || 'Failed to create role.');
            }
        } catch (error) {
            const message = getErrorMessage(error);
            errorMessage(message || 'An unexpected error occurred.');
        } finally {
            setLoading((prev) => ({ ...prev, create: false }));
        }
    };

    const handleUpdate = async (values: { roleDescription: string; displayName: string }) => {
        setLoading((prev) => ({ ...prev, update: true }));

        if (editRole) {
            try {
                const response = await updateRole({
                    role_name: editRole.role_name,
                    role_description: values.roleDescription,
                    display_name: values.displayName,
                });

                if (response.status) {
                    setRoles((prevRoles) =>
                        prevRoles.map((role) =>
                            role.role_name === editRole.role_name
                                ? { ...role, role_description: values.roleDescription, display_name: values.displayName }
                                : role
                        )
                    );
                    successMessage(response.message);
                } else {
                    errorMessage(response.message || 'Failed to update role.');
                }
            } catch (error) {
                const message = getErrorMessage(error);
                errorMessage(message || 'An unexpected error occurred.');
            } finally {
                setLoading((prev) => ({ ...prev, update: false }));
            }
        }
    };

    const handleDeleteConfirm = async () => {
        setLoading((prev) => ({ ...prev, delete: true }));
        if (itemToDelete) {
            try {
                const response = await deleteRole(itemToDelete.row.role_name);

                if (response.status) {
                    setRoles((prevRoles) =>
                        prevRoles.filter((role) => role.role_name !== itemToDelete.row.role_name)
                    );
                    successMessage(response.message);
                } else {
                    errorMessage(response.message || 'Failed to delete role.');
                }
            } catch (error) {
                const message = getErrorMessage(error);
                errorMessage(message || 'An unexpected error occurred.');
            } finally {
                setLoading((prev) => ({ ...prev, delete: false }));
                setDeleteOpen(false);
            }
        }
    };

    return (
        <>
            {loading.fetch ? (
                <Loader color="primary" size={40} />
            ) : (
                <Box style={{ width: '100%', height: '50%' }}>
                    <main className="m-a-20">
                        <div>
                            <div className="d-flex justify-end">
                                <div className="d-flex justify-between w-100">
                                    <TextField
                                        className="w-25"
                                        label="Search"
                                        variant="outlined"
                                        value={searchQuery}
                                        size="small"
                                        startIcon={<SearchIcon />}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                    <Button
                                        label="Add"
                                        variant="contained"
                                        color="primary"
                                        onClick={openDialog}
                                    />
                                </div>
                            </div>
                        </div>
                        <div style={{ height: '100%', width: '100%' }} className="ag-theme-alpine m-t-20">
                            <GridTable
                                rows={roles}
                                columns={columns}
                                pageSize={10}
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                getRowId={(row) => row.role_name}
                                density="standard"
                            />
                        </div>
                        <Formik
                            initialValues={{
                                roleName: editRole ? editRole.role_name : "",
                                roleDescription: editRole ? editRole.role_description : "",
                                displayName: editRole ? editRole.display_name : "",
                            }}
                            validationSchema={validationSchema}
                            onSubmit={confirmDialog}
                            enableReinitialize
                        >
                            {({ values,
                                errors,
                                touched,
                                handleChange,
                                handleSubmit,
                                resetForm,
                                setFieldValue
                            }) => (
                                <Dialog
                                    open={isDialogOpen}
                                    onClose={() => closeDialog(resetForm)}
                                    onConfirm={handleSubmit}
                                    title={
                                        !loading.update && !loading.create
                                            ? editRole
                                                ? "Edit Role"
                                                : "Add New Role"
                                            : ""
                                    }
                                    confirmButtonText={
                                        !loading.update && !loading.create
                                            ? editRole
                                                ? "Update"
                                                : "Add"
                                            : ""
                                    }
                                    cancelButtonText={!loading.update && !loading.create ? "Cancel" : ""}
                                    icon={<BadgeOutlinedIcon />}
                                    message={
                                        <Box padding={4}>
                                            {loading.update || loading.create ? (
                                                <Loader color="primary" size={40} />
                                            ) : (
                                                <Form>
                                                    <Grid container spacing={2}>
                                                        <Grid size={4}>
                                                            {roleNames?.length > 0 && (
                                                                <Dropdown
                                                                    options={roleNames}
                                                                    value={values.roleName ?? ""}
                                                                    onChange={(value) => setFieldValue("roleName", value)}
                                                                    label="Role Name"
                                                                    width="200px"
                                                                    required
                                                                    disabled={!!editRole}
                                                                    error={Boolean(touched.roleName && errors.roleName)}
                                                                    helperText={touched.roleName && errors.roleName ? String(errors.roleName) : undefined}
                                                                />
                                                            )}
                                                        </Grid>
                                                        <Grid size={4}>
                                                            <TextField
                                                                id="roleDescription"
                                                                name="roleDescription"
                                                                label="Role Description"
                                                                value={values.roleDescription}
                                                                onChange={handleChange}
                                                                required
                                                                error={touched.roleDescription && Boolean(errors.roleDescription)}
                                                                helperText={touched.roleDescription && errors.roleDescription}
                                                            />
                                                        </Grid>
                                                        <Grid size={4}>
                                                            <TextField
                                                                id="displayName"
                                                                name="displayName"
                                                                label="Display Name"
                                                                required
                                                                value={values.displayName}
                                                                onChange={handleChange}
                                                                error={touched.displayName && Boolean(errors.displayName)}
                                                                helperText={touched.displayName && errors.displayName}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Form>
                                            )}
                                        </Box>
                                    }
                                />
                            )}
                        </Formik>
                        <Dialog
                            open={deleteOpen}
                            onClose={handleDeleteCancel}
                            onConfirm={handleDeleteConfirm}
                            title={!loading.delete ? "Delete Role" : ""}
                            confirmButtonText={!loading.delete ? "Confirm" : ""}
                            cancelButtonText={!loading.delete ? "Cancel" : ""}
                            icon={<DeleteIcon />}
                            message={
                                <Box padding={2}>
                                    {loading.delete ? (
                                        <Loader color="primary" size={40} />
                                    ) : (
                                        "Are you sure you want to delete this role?"
                                    )}
                                </Box>
                            }
                        />
                    </main>
                </Box>
            )}
        </>
    );
};

export default Role;
